// Search query — one pure engine for the palette/browser (P17,
// federated-search.md §5).
//
// The palette's matcher was a blunt case-insensitive substring filter. This
// replaces it with (1) a TOTAL parser from raw text into a typed `SearchQuery`
// sum and (2) an fzf-style fuzzy scorer. MVP grammar: bare words are fuzzy
// text terms; `"quoted"` is an exact-substring term; `type:<kind>` narrows to a
// resource kind (an unknown kind degrades to a text term); `by:<actor>`
// narrows to who authored a moment (the History axis — `by:me`/`by:assistant`/
// `by:app`, unknown actor degrades to text). Space = AND. The
// full DSL (`in:`/`from-source:`/`before:`/negation) and content search defer
// (federated-search.md §6) — shipping a qualifier that silently searched only
// titles would be a lie.
//
// `parseQuery` is TOTAL by design: a search box mid-keystroke is always a valid
// query, so an unknown qualifier or a lone `type:` degrades to text rather than
// erroring — degradation-to-text IS the parse boundary (no error channel). The
// scorer is subsequence-with-bonuses (the VS Code / fzf family), never
// Levenshtein (which punishes the sparse abbreviations palette users type). Our
// corpus is 10²–10³ titles, so a per-keystroke scan is free — no index, no
// debounce.

export type {
    ByActor,
    ByTerm,
    ExactTerm,
    FuzzyMatch,
    KindTerm,
    MatchRange,
    Scored,
    SearchItem,
    SearchKind,
    SearchQuery,
    SearchTerm,
    TextTerm,
    Token,
};
export {
    evaluate,
    isSearchKind,
    parseQuery,
    SEARCH_ACTORS,
    SEARCH_KINDS,
    scoreFuzzy,
    termOf,
    tokenize,
};

// The resource kinds `type:` may name — mirrors the palette's CommandKind
// resource subset (the flat pool's four object kinds — flattened-object-
// model-design.md §5.1: `project` LEFT this sum when Project retired as a
// container; `conversation` JOINED it, since Conversation is now a
// first-class flat-pool object needing the same findability treatment as
// Source/Material/Package; commands are not a `type:` target in MVP).
type SearchKind = "source" | "material" | "export" | "conversation";

const SEARCH_KINDS: readonly SearchKind[] = [
    "source",
    "material",
    "export",
    "conversation",
];

const isSearchKind = (s: string): s is SearchKind =>
    (SEARCH_KINDS as readonly string[]).includes(s);

// The actor `by:` may name — the ONE axis History adds that the palette lacks
// (federated-search.md §5a / synthesis S4). `by:me` / `by:assistant` /
// `by:app` narrow the timeline to who authored a moment; a named qualifier,
// never a flag. Items that carry no `actor` (the palette's resource items)
// simply never match a `by:` term, so this stays inert for non-history corpora.
type ByActor = "me" | "assistant" | "app";

const SEARCH_ACTORS: readonly ByActor[] = ["me", "assistant", "app"];

const isByActor = (s: string): s is ByActor =>
    (SEARCH_ACTORS as readonly string[]).includes(s);

// One named variant per term kind — sums all the way up (MVP subset).
type TextTerm = { readonly type: "text"; readonly value: string }; // fuzzy
type ExactTerm = { readonly type: "exact"; readonly value: string }; // "quoted"
// `type:source` names one kind; `type:source,material` names several (comma-OR
// within the axis, P17b) — so the term carries a LIST, and an item matches when
// its kind is any of them. Always non-empty (a term with no known kind degrades
// to text in `termOf`).
type KindTerm = {
    readonly type: "kind";
    readonly kinds: readonly SearchKind[];
};
// `by:me` names one actor; `by:me,assistant` names several (comma-OR within
// the axis, mirroring `type:`). Always non-empty — a `by:` with no known actor
// degrades to text in `termOf`, exactly as an unknown `type:` does.
type ByTerm = {
    readonly type: "by";
    readonly actors: readonly ByActor[];
};
type SearchTerm = TextTerm | ExactTerm | KindTerm | ByTerm;

// Space = AND over the terms.
type SearchQuery = { readonly terms: readonly SearchTerm[] };

// Split raw input into tokens, keeping a `"quoted phrase"` as one token
// (quotes stripped) and marking it exact. A dangling opening quote consumes the
// rest of the line (mid-typing is still a valid query).
type Token = { readonly text: string; readonly quoted: boolean };

const tokenize = (raw: string): readonly Token[] => {
    const tokens: Token[] = [];
    let i = 0;
    const n = raw.length;
    while (i < n) {
        while (i < n && raw[i] === " ") i += 1;
        if (i >= n) break;
        if (raw[i] === '"') {
            i += 1;
            const start = i;
            while (i < n && raw[i] !== '"') i += 1;
            tokens.push({ text: raw.slice(start, i), quoted: true });
            if (i < n) i += 1; // consume the closing quote
        } else {
            const start = i;
            while (i < n && raw[i] !== " ") i += 1;
            tokens.push({ text: raw.slice(start, i), quoted: false });
        }
    }
    return tokens.filter((token) => token.text.length > 0);
};

// One token → one term. A quoted token is exact. A `type:<kind>` (or
// comma-list `type:a,b`) token is a kind term over every KNOWN kind named,
// else it degrades to a text term (the whole `type:...` string, so mid-typing
// still searches). Everything else is a fuzzy text term.
const termOf = (token: Token): SearchTerm => {
    if (token.quoted) return { type: "exact", value: token.text };
    const colon = token.text.indexOf(":");
    if (colon > 0) {
        const qualifier = token.text.slice(0, colon);
        const rest = token.text.slice(colon + 1);
        if (qualifier === "type") {
            const kinds = rest.split(",").filter(isSearchKind);
            if (kinds.length > 0) return { type: "kind", kinds };
        }
        if (qualifier === "by") {
            const actors = rest.split(",").filter(isByActor);
            if (actors.length > 0) return { type: "by", actors };
        }
    }
    return { type: "text", value: token.text };
};

const parseQuery = (raw: string): SearchQuery => ({
    terms: tokenize(raw).map(termOf),
});

// === Fuzzy scorer ============================================================
// In-order subsequence match with bonuses: word-boundary start, consecutive
// runs, and a shorter-title tiebreak. Returns null when `query` is not a
// subsequence of `target` (no match); a higher `score` is a better match.
//
// The ONE house scorer, shared by the omnibar (`evaluate`, which reads only
// `.score`) and the content-finder (`components/Finder`, which also reads the
// match `.ranges` to highlight each hit character-by-character). It returns the
// score AND the coalesced match ranges in a single pass so the two surfaces
// rank identically — the finder never ships a divergent second scorer
// (.claude/analysis/fuzzy-finder.md §4a/§5). A `MatchRange` is a half-open
// interval `[start, end)` into the TARGET string.

// A half-open interval `[start, end)` into the target — the characters a hit
// covers. Its own named type, not a bare tuple.
type MatchRange = { readonly start: number; readonly end: number };
// The scorer's result: a comparable score (higher = better) plus the ranges to
// highlight. Absence of a match is the `null` return, never a zero-score
// `FuzzyMatch` — callers narrow on `null`, and a real `FuzzyMatch` always
// describes an actual subsequence hit.
type FuzzyMatch = {
    readonly score: number;
    readonly ranges: readonly MatchRange[];
};

// A word boundary is the start of the string or a separator before the char.
// Includes `/` (a path separator) so a hit at a path segment start scores like
// one at a word start — the fzy/fzf convention (docs §2).
const isBoundary = (ch: string | undefined): boolean =>
    ch === undefined ||
    ch === " " ||
    ch === "-" ||
    ch === "_" ||
    ch === "." ||
    ch === "/";

// Fold a sorted list of matched indices into half-open ranges, coalescing
// adjacent indices (i, i+1, …) into one interval — so a consecutive run paints
// as a single mark, not one <mark> per character.
const coalesce = (indices: readonly number[]): readonly MatchRange[] => {
    if (indices.length === 0) return [];
    const ranges: MatchRange[] = [];
    let start = indices[0];
    let prev = indices[0];
    for (let i = 1; i < indices.length; i += 1) {
        const cur = indices[i];
        if (cur === prev + 1) {
            prev = cur;
            continue;
        }
        ranges.push({ start, end: prev + 1 });
        start = cur;
        prev = cur;
    }
    ranges.push({ start, end: prev + 1 });
    return ranges;
};

const scoreFuzzy = (query: string, target: string): FuzzyMatch | null => {
    const q = query.toLowerCase();
    const t = target.toLowerCase();
    // An empty query matches over nothing (score 0, no ranges) — every
    // candidate passes, input order preserved.
    if (q === "") return { score: 0, ranges: [] };
    const matched: number[] = [];
    let score = 0;
    let ti = 0;
    let prevMatched = -2;
    for (let qi = 0; qi < q.length; qi += 1) {
        const ch = q[qi];
        let found = -1;
        for (let k = ti; k < t.length; k += 1) {
            if (t[k] === ch) {
                found = k;
                break;
            }
        }
        if (found === -1) return null; // not a subsequence
        score += 1;
        if (found === prevMatched + 1) score += 5; // consecutive run
        if (isBoundary(t[found - 1])) score += 8; // word-boundary start
        matched.push(found);
        prevMatched = found;
        ti = found + 1;
    }
    // Shorter titles win ties (a tiny, bounded nudge).
    const tiebreak = Math.max(0, 10 - target.length) * 0.1;
    return { score: score + tiebreak, ranges: coalesce(matched) };
};

// === Evaluate ================================================================
// The minimum an item needs to be searched: id (passthrough), title (scored),
// and a kind (the `type:` filter). Structural — CommandItem fits.
type SearchItem = {
    readonly id: string;
    readonly title: string;
    readonly kind: string;
    // The moment's actor, for the `by:` axis. Optional: the palette's resource
    // items carry none (and so are never narrowed by a `by:` term); history
    // rows carry one. A ByActor string ("me" | "assistant" | "app").
    readonly actor?: string;
};

type Scored<A> = { readonly item: A; readonly score: number };

// Evaluate a query over items: KindTerms filter by membership — an item passes
// the kind gate when its kind is ANY of the kinds named across all `type:`
// terms (OR, whether written `type:a,b` or `type:a type:b`), ExactTerms require
// a case-insensitive substring, and
// TextTerms score fuzzily (summed). An item passes only if every text/exact
// term matches; the result is sorted by descending score. An empty query
// returns every item unscored (score 0), preserving input order.
const evaluate = <A extends SearchItem>(
    query: SearchQuery,
    items: readonly A[],
): readonly Scored<A>[] => {
    // Widened to string for the membership test so `item.kind` (a plain
    // string) compares without a cast.
    const kinds: readonly string[] = query.terms.flatMap((term) =>
        term.type === "kind" ? term.kinds : [],
    );
    const actors: readonly string[] = query.terms.flatMap((term) =>
        term.type === "by" ? term.actors : [],
    );
    const exacts = query.terms.flatMap((term) =>
        term.type === "exact" ? [term.value.toLowerCase()] : [],
    );
    const texts = query.terms.flatMap((term) =>
        term.type === "text" ? [term.value] : [],
    );
    const scored: Scored<A>[] = [];
    for (const item of items) {
        if (kinds.length > 0 && !kinds.includes(item.kind)) {
            continue;
        }
        // `by:` gate — an item passes when its actor is any named actor (OR
        // within the axis). An item with no actor never passes a `by:` term.
        if (
            actors.length > 0 &&
            (item.actor === undefined || !actors.includes(item.actor))
        ) {
            continue;
        }
        const title = item.title.toLowerCase();
        // The id is matchable by EXACT SUBSTRING only (never fuzzy): a resource's
        // id is a UUID, so pasting it (or a chunk) into the omnibar resolves the
        // object even when its title isn't remembered — the affordance a chat-
        // created material's card removes the NEED for, but findability still
        // wants (chat-as-live-work-surface §Axis 2). Fuzzy-matching a hex id would
        // subsequence-match almost any short query, so id hits are exact + length-
        // gated (≥ 4) — normal words never collide with a hex id, a pasted id does.
        const id = item.id.toLowerCase();
        const idMatches = (value: string): boolean =>
            value.length >= 4 && id.includes(value);
        if (!exacts.every((value) => title.includes(value) || idMatches(value)))
            continue;
        let score = 0;
        let matched = true;
        for (const text of texts) {
            const m = scoreFuzzy(text, item.title);
            if (m === null) {
                if (!idMatches(text.toLowerCase())) {
                    matched = false;
                    break;
                }
                // An id-only hit contributes no fuzzy score — it ranks below any
                // title match, so pasting an id surfaces the object without
                // reordering genuine title results.
                continue;
            }
            score += m.score;
        }
        if (!matched) continue;
        scored.push({ item, score });
    }
    return scored.sort((a, b) => b.score - a.score);
};
